import { useCallback, MutableRefObject } from 'react';
import type { Virtualizer } from '@tanstack/react-virtual';

/** Matches @tanstack/virtual-core's ScrollAlignment (not re-exported from react-virtual). */
type ScrollAlignment = 'start' | 'center' | 'end' | 'auto';

// Iteratively scroll to (index, align) until scrollHeight + scrollTop
// both stop changing for one paint cycle. Needed because scrollToIndex
// uses estimateSize for unmeasured rows; each pass brings new rows
// into the viewport, ResizeObserver measures them on the next paint,
// and the following pass re-projects. Wall-clock cap defends against
// pathological re-measure loops (e.g., slow image loads) so
// suppressSnapBackRef can't get stuck.
const WALL_CLOCK_CAP_MS = 1000;

export interface UseScrollAndStabilizeArgs {
    rowVirtualizer: Pick<Virtualizer<HTMLDivElement, Element>, 'scrollToIndex' | 'scrollToOffset'>;
    /** combinedData.length — the index of the absolute last row is this minus 1. */
    combinedDataLength: number;
    scrollContainerRef: MutableRefObject<HTMLDivElement | null>;
    suppressSnapBackRef: MutableRefObject<boolean>;
    /**
     * Injectable clock seam for deterministic testing of the wall-clock cap.
     * Defaults to the real `Date.now`; production behavior is unchanged.
     */
    now?: () => number;
}

/**
 * Returns a stable `scrollAndStabilize(scrollIndex, align)` that iteratively
 * scrolls until layout converges (or the wall-clock cap trips) and returns a
 * cancel fn. Extracted verbatim from `additional-info-renderer.tsx` to keep the
 * dep-array semantics (`[rowVirtualizer, combinedDataLength]`) and behavior
 * identical — this hook is behavior-preserving.
 */
export function useScrollAndStabilize({
    rowVirtualizer,
    combinedDataLength,
    scrollContainerRef,
    suppressSnapBackRef,
    now = Date.now,
}: UseScrollAndStabilizeArgs): (
    scrollIndex: number,
    align: ScrollAlignment,
    onSettle?: () => void
) => () => void {
    return useCallback(
        (scrollIndex: number, align: ScrollAlignment, onSettle?: () => void): (() => void) => {
            let cancelled = false;
            let lastScrollHeight = -1;
            let lastScrollTop = -1;
            const startTime = now();
            let outerHandle = 0;
            let innerHandle = 0;

            suppressSnapBackRef.current = true;
            // Fires on convergence / wall-clock cap, NOT on cancel — lets callers
            // defer work (clearing pendingScrollTargetId) until the scroll lands,
            // not when it's merely kicked off (prevent multiple fetches).
            const releaseSuppression = (): void => {
                suppressSnapBackRef.current = false;
                onSettle?.();
            };

            // When the target is the absolute last row, read scrollHeight
            // directly — scrollToIndex's projection underestimates when
            // the measurementsCache is stale (font load, dynamic content).
            const isScrollToListBottom = align === 'end' && scrollIndex === combinedDataLength - 1;

            const attempt = (): void => {
                if (cancelled) return;
                if (isScrollToListBottom && scrollContainerRef.current !== null) {
                    const el = scrollContainerRef.current;
                    const maxOffset = el.scrollHeight - el.clientHeight;
                    rowVirtualizer.scrollToOffset(maxOffset, { align: 'start' });
                } else {
                    rowVirtualizer.scrollToIndex(scrollIndex, { align });
                }
                if (now() - startTime >= WALL_CLOCK_CAP_MS) {
                    releaseSuppression();
                    return;
                }
                outerHandle = requestAnimationFrame(() => {
                    if (cancelled) return;
                    innerHandle = requestAnimationFrame(() => {
                        if (cancelled) return;
                        const el = scrollContainerRef.current;
                        if (el === null) {
                            releaseSuppression();
                            return;
                        }
                        const scrollHeight = el.scrollHeight;
                        const scrollTop = el.scrollTop;
                        if (scrollHeight === lastScrollHeight && scrollTop === lastScrollTop) {
                            releaseSuppression();
                            return;
                        }
                        lastScrollHeight = scrollHeight;
                        lastScrollTop = scrollTop;
                        attempt();
                    });
                });
            };

            attempt();

            return (): void => {
                cancelled = true;
                suppressSnapBackRef.current = false;
                if (outerHandle !== 0) cancelAnimationFrame(outerHandle);
                if (innerHandle !== 0) cancelAnimationFrame(innerHandle);
            };
        },
        // scrollContainerRef / suppressSnapBackRef are stable ref objects and
        // `now` defaults to the stable `Date.now`, so listing them is a no-op for
        // callback identity — it just lets exhaustive-deps verify stability now
        // that these arrive as params rather than in-component useRef locals.
        [rowVirtualizer, combinedDataLength, scrollContainerRef, suppressSnapBackRef, now]
    );
}
